import React, { useState, useEffect } from 'react';
import {
  Table,
  Button,
  Space,
  Modal,
  Form,
  Input,
  InputNumber,
  Select,
  message,
  Card,
  Popconfirm,
  Tooltip,
  Statistic,
  Row,
  Col,
  Tabs,
  Divider
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SyncOutlined,
  SendOutlined
} from '@ant-design/icons';
import axios from 'axios';
import { aiProviderApi } from '../services/api';

const { Option } = Select;
const { TabPane } = Tabs;
const { TextArea } = Input;

const AIProviders = () => {
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [form] = Form.useForm();
  const [editingId, setEditingId] = useState(null);
  const [stats, setStats] = useState({});
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [chatMessages, setChatMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [chatLoading, setChatLoading] = useState(false);

  const modelOptions = {
    deepseek: [
      { value: 'deepseek-reasoner', label: 'deepseek-reasoner' },
      { value: 'deepseek-chat', label: 'deepseek-chat' },
      { value: 'deepseek-ai/DeepSeek-V3', label: 'deepseek-ai/DeepSeek-V3' },
      { value: 'deepseek-ai/DeepSeek-V3-0324', label: 'deepseek-ai/DeepSeek-V3-0324' },
    ],
    openai: [
      { value: 'gpt-3.5-turbo', label: 'gpt-3.5-turbo' },
      { value: 'gpt-4', label: 'gpt-4' },
      { value: 'gpt-4o', label: 'gpt-4o' },
    ],
    // 可扩展其他类型
  };

  const [providerType, setProviderType] = useState();

  useEffect(() => {
    fetchProviders();
  }, []);

  const fetchProviders = async () => {
    try {
      setLoading(true);
      const response = await aiProviderApi.getProviders();
      setProviders(response.data || response.items || response || []);
    } catch (error) {
      message.error('获取AI提供商列表失败');
      setProviders([]);
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setEditingId(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEdit = (record) => {
    setEditingId(record.id);
    form.setFieldsValue(record);
    setModalVisible(true);
  };

  const handleDelete = async (id) => {
    try {
      await aiProviderApi.deleteProvider(id);
      message.success('删除成功');
      fetchProviders();
    } catch (error) {
      message.error('删除失败');
    }
  };

  const handleTest = async (id) => {
    try {
      await aiProviderApi.testProvider(id);
      message.success('连接测试成功');
    } catch (error) {
      message.error('连接测试失败');
    }
  };

  const onFinish = async (values) => {
    try {
      if (editingId) {
        await aiProviderApi.updateProvider(editingId, values);
        message.success('更新成功');
      } else {
        await aiProviderApi.createProvider(values);
        message.success('添加成功');
      }
      setModalVisible(false);
      fetchProviders();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || '操作失败';
      message.error(errorMsg);
    }
  };

  const fetchStats = async (id) => {
    try {
      const response = await aiProviderApi.getProvider(id);
      setStats(prev => ({ ...prev, [id]: response }));
    } catch (error) {
      message.error('获取统计信息失败');
    }
  };

  const handleSendMessage = async () => {
    if (!inputMessage.trim() || !selectedProvider) return;

    const newMessage = { role: 'user', content: inputMessage };
    setChatMessages(prev => [...prev, newMessage]);
    setInputMessage('');
    setChatLoading(true);

    try {
      const response = await aiProviderApi.testProvider(selectedProvider.id);
      setChatMessages(prev => [...prev, { role: 'assistant', content: '测试调用成功' }]);
    } catch (error) {
      message.error('发送消息失败');
    } finally {
      setChatLoading(false);
    }
  };

  const columns = [
    {
      title: '名称',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: '提供商类型',
      dataIndex: 'provider_type',
      key: 'provider_type',
    },
    {
      title: '模型类型',
      dataIndex: 'model_type',
      key: 'model_type',
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <span style={{ color: status === 'active' ? 'green' : 'red' }}>
          {status === 'active' ? '活跃' : '停用'}
        </span>
      ),
    },
    {
      title: '操作',
      key: 'action',
      render: (_, record) => (
        <Space size="middle">
          <Tooltip title="编辑">
            <Button
              type="primary"
              icon={<EditOutlined />}
              onClick={() => handleEdit(record)}
            />
          </Tooltip>
          <Tooltip title="测试连接">
            <Button
              icon={<SyncOutlined />}
              onClick={() => handleTest(record.id)}
            />
          </Tooltip>
          <Tooltip title="使用此模型">
            <Button
              type="primary"
              icon={<SendOutlined />}
              onClick={() => setSelectedProvider(record)}
            />
          </Tooltip>
          <Popconfirm
            title="确定要删除这个提供商吗？"
            onConfirm={() => handleDelete(record.id)}
          >
            <Button
              danger
              icon={<DeleteOutlined />}
            />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div className="p-6">
      <Row gutter={16}>
        <Col span={selectedProvider ? 16 : 24}>
          <Card title="AI提供商管理" className="mb-6">
            <div className="mb-4">
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={handleAdd}
              >
                添加提供商
              </Button>
            </div>

            <Table
              columns={columns}
              dataSource={providers}
              rowKey="id"
              loading={loading}
              expandable={{
                expandedRowRender: (record) => {
                  const providerStats = stats[record.id] || {};
                  return (
                    <Row gutter={16}>
                      <Col span={6}>
                        <Statistic
                          title="总请求数"
                          value={providerStats.total_requests || 0}
                        />
                      </Col>
                      <Col span={6}>
                        <Statistic
                          title="成功率"
                          value={providerStats.success_rate || 0}
                          suffix="%"
                        />
                      </Col>
                      <Col span={6}>
                        <Statistic
                          title="平均响应时间"
                          value={providerStats.average_response_time || 0}
                          suffix="ms"
                        />
                      </Col>
                      <Col span={6}>
                        <Statistic
                          title="错误数"
                          value={providerStats.error_count || 0}
                        />
                      </Col>
                    </Row>
                  );
                },
                onExpand: (expanded, record) => {
                  if (expanded) {
                    fetchStats(record.id);
                  }
                },
              }}
            />
          </Card>
        </Col>

        {selectedProvider && (
          <Col span={8}>
            <Card
              title={`与 ${selectedProvider.name} 对话`}
              extra={
                <Button
                  type="link"
                  onClick={() => setSelectedProvider(null)}
                >
                  关闭
                </Button>
              }
            >
              <div
                style={{
                  height: '400px',
                  overflowY: 'auto',
                  marginBottom: '16px',
                  padding: '16px',
                  border: '1px solid #f0f0f0',
                  borderRadius: '4px'
                }}
              >
                {chatMessages.map((msg, index) => (
                  <div
                    key={index}
                    style={{
                      marginBottom: '16px',
                      padding: '8px',
                      backgroundColor: msg.role === 'user' ? '#f0f0f0' : '#e6f7ff',
                      borderRadius: '4px'
                    }}
                  >
                    <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>
                      {msg.role === 'user' ? '用户' : 'AI助手'}
                    </div>
                    <div style={{ whiteSpace: 'pre-wrap' }}>{msg.content}</div>
                  </div>
                ))}
              </div>
              <div style={{ display: 'flex', gap: '8px' }}>
                <TextArea
                  value={inputMessage}
                  onChange={(e) => setInputMessage(e.target.value)}
                  placeholder="输入消息..."
                  autoSize={{ minRows: 2, maxRows: 6 }}
                  onPressEnter={(e) => {
                    if (!e.shiftKey) {
                      e.preventDefault();
                      handleSendMessage();
                    }
                  }}
                />
                <Button
                  type="primary"
                  icon={<SendOutlined />}
                  onClick={handleSendMessage}
                  loading={chatLoading}
                >
                  发送
                </Button>
              </div>
            </Card>
          </Col>
        )}
      </Row>

      <Modal
        title={editingId ? '编辑提供商' : '添加提供商'}
        visible={modalVisible}
        onOk={form.submit}
        onCancel={() => setModalVisible(false)}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={onFinish}
        >
          <Form.Item
            name="name"
            label="名称"
            rules={[{ required: true, message: '请输入提供商名称' }]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="provider_type"
            label="提供商类型"
            rules={[{ required: true, message: '请选择提供商类型' }]}
          >
            <Select
              placeholder="请选择提供商类型"
              allowClear
              onChange={value => {
                setProviderType(value);
                form.setFieldsValue({ model_type: undefined }); // 切换时清空模型类型
              }}
            >
              <Option value="deepseek">DeepSeek</Option>
              <Option value="openai">OpenAI</Option>
              <Option value="anthropic">Anthropic</Option>
              <Option value="gemini">Gemini</Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="model_type"
            label="模型类型"
            rules={[{ required: true, message: '请选择模型类型' }]}
          >
            <Select
              placeholder="请选择模型类型"
              allowClear
              options={modelOptions[providerType] || []}
              showSearch
              filterOption={(input, option) => option.label.toLowerCase().includes(input.toLowerCase())}
            />
          </Form.Item>

          <Form.Item
            name="api_key"
            label="API密钥"
            rules={[{ required: true, message: '请输入API密钥' }]}
          >
            <Input.Password />
          </Form.Item>

          <Form.Item
            name="api_base"
            label="API基础URL"
            rules={[{ required: false }]}
          >
            <Input placeholder="例如 https://api.openai.com/v1" />
          </Form.Item>

          <Form.Item
            name="status"
            label="状态"
            rules={[{ required: true, message: '请选择状态' }]}
          >
            <Select>
              <Option value="active">活跃</Option>
              <Option value="inactive">停用</Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="description"
            label="描述"
            rules={[{ required: false }]}
          >
            <TextArea rows={3} placeholder="对该配置进行一些描述" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default AIProviders; 